import { Dispatch } from 'redux';

import {
  apiFetchNotifications,
  apiMarkNotificationsAsDismissed,
  apiMarkNotificationsAsRead,
  apiMarkNotificationsAsViewed,
} from 'api/notification/apiUserNotificationEndpoints';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { sortNotifications } from 'utils/userNotifications';

// Types

export type MainStoreUserNotificationsState = {
  notifications: MappedNotification[];
  isFetching?: boolean;
  isLoaded: boolean;
};

type FetchNotificationsRequestAction = {
  type: 'NOTIFICATIONS_FETCH_REQUEST';
};

type FetchNotificationsSuccessAction = {
  type: 'NOTIFICATIONS_FETCH_SUCCESS';
  notifications: MappedNotification[];
};

type MarkNotificationsViewedRequestAction = {
  type: 'NOTIFICATIONS_MARK_VIEWED_REQUEST';
};

type MarkNotificationsViewedSuccessAction = {
  type: 'NOTIFICATIONS_MARK_VIEWED_SUCCESS';
  notifications: MappedNotification[];
};

type MarkNotificationsReadRequestAction = {
  type: 'NOTIFICATIONS_MARK_READ_REQUEST';
};

type MarkNotificationsReadSuccessAction = {
  type: 'NOTIFICATIONS_MARK_READ_SUCCESS';
  notifications: MappedNotification[];
};

type MarkNotificationsDismissedRequestAction = {
  type: 'NOTIFICATIONS_MARK_DISMISSED_REQUEST';
};

type MarkNotificationsDismissedSuccessAction = {
  type: 'NOTIFICATIONS_MARK_DISMISSED_SUCCESS';
  notifications: MappedNotification[];
};

export type MainStoreUserNotificationsAction =
  | FetchNotificationsRequestAction
  | FetchNotificationsSuccessAction
  | MarkNotificationsViewedRequestAction
  | MarkNotificationsViewedSuccessAction
  | MarkNotificationsReadRequestAction
  | MarkNotificationsReadSuccessAction
  | MarkNotificationsDismissedRequestAction
  | MarkNotificationsDismissedSuccessAction;

// Reducer

export function userNotificationsReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreUserNotificationsState = {
    notifications: [],
    isLoaded: false,
  },
  action: MainStoreUserNotificationsAction,
) {
  switch (action.type) {
    case 'NOTIFICATIONS_FETCH_REQUEST':
      return { ...state, isFetching: true };

    case 'NOTIFICATIONS_FETCH_SUCCESS':
      return {
        ...state,
        notifications: action.notifications,
        isFetching: false,
        isLoaded: true,
      };

    case 'NOTIFICATIONS_MARK_VIEWED_SUCCESS':
      return {
        ...state,
        notifications: action.notifications,
      };

    case 'NOTIFICATIONS_MARK_READ_SUCCESS':
      return {
        ...state,
        notifications: action.notifications,
      };

    case 'NOTIFICATIONS_MARK_DISMISSED_SUCCESS':
      return {
        ...state,
        notifications: action.notifications,
      };

    default:
      return state;
  }
}

// Actions

const fetchNotificationsRequest = (): FetchNotificationsRequestAction => ({
  type: 'NOTIFICATIONS_FETCH_REQUEST',
});

const markNotificationsViewedRequest =
  (): MarkNotificationsViewedRequestAction => ({
    type: 'NOTIFICATIONS_MARK_VIEWED_REQUEST',
  });

const markNotificationsReadRequest =
  (): MarkNotificationsReadRequestAction => ({
    type: 'NOTIFICATIONS_MARK_READ_REQUEST',
  });

const markNotificationsDismissedRequest =
  (): MarkNotificationsDismissedRequestAction => ({
    type: 'NOTIFICATIONS_MARK_DISMISSED_REQUEST',
  });

const markNotificationsViewedSuccess = (
  notifications: MappedNotification[],
): MarkNotificationsViewedSuccessAction => ({
  type: 'NOTIFICATIONS_MARK_VIEWED_SUCCESS',
  notifications,
});

const markNotificationsReadSuccess = (
  notifications: MappedNotification[],
): MarkNotificationsReadSuccessAction => ({
  type: 'NOTIFICATIONS_MARK_READ_SUCCESS',
  notifications,
});

const markNotificationsDismissedSuccess = (
  notifications: MappedNotification[],
): MarkNotificationsDismissedSuccessAction => ({
  type: 'NOTIFICATIONS_MARK_DISMISSED_SUCCESS',
  notifications,
});

const fetchNotificationsSuccess = (
  notifications: MappedNotification[],
): FetchNotificationsSuccessAction => ({
  type: 'NOTIFICATIONS_FETCH_SUCCESS',
  notifications,
});

export const fetchNotifications =
  () => (dispatch: Dispatch<MainStoreUserNotificationsAction>) => {
    dispatch(fetchNotificationsRequest());
    return apiFetchNotifications().then((notifications) => {
      dispatch(fetchNotificationsSuccess(sortNotifications(notifications)));
    });
  };

export const markNotificationsAsViewed =
  (ids: Array<string>) =>
  (dispatch: Dispatch<MainStoreUserNotificationsAction>) => {
    dispatch(markNotificationsViewedRequest());
    return apiMarkNotificationsAsViewed(ids).then((notifications) => {
      dispatch(
        markNotificationsViewedSuccess(sortNotifications(notifications)),
      );
    });
  };

export const markNotificationsAsRead =
  (ids: Array<string>) =>
  (dispatch: Dispatch<MainStoreUserNotificationsAction>) => {
    dispatch(markNotificationsReadRequest());
    return apiMarkNotificationsAsRead(ids).then((notifications) => {
      dispatch(markNotificationsReadSuccess(sortNotifications(notifications)));
    });
  };

export const markNotificationsAsDismissed =
  (ids: Array<string>) =>
  (dispatch: Dispatch<MainStoreUserNotificationsAction>) => {
    dispatch(markNotificationsDismissedRequest());
    return apiMarkNotificationsAsDismissed(ids).then((notifications) => {
      dispatch(
        markNotificationsDismissedSuccess(sortNotifications(notifications)),
      );
    });
  };
